import { StyleSheet, css } from 'aphrodite'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useUser } from '../contexts/UserContext'
import { userService } from '../services/userService'
import type {
  Contact,
  ContactCreate,
  StakeholderType,
  User,
} from '../types/user'
import MultiSelect, { type Option } from './MultiSelect'
import SaveToDirectoryDialog from './contact/SaveToDirectoryDialog'

export interface ContactInfo {
  id?: number
  firstName: string
  lastName: string
  email: string
  phone?: string
  companyName?: string
  companyAddress?: string
  // Track if this is from directory or manually entered
  isFromDirectory: boolean
}

interface ContactAutocompleteProps {
  /** Label for the contact field */
  label: string
  /** Currently selected contact info */
  value: ContactInfo | null
  /** Callback when contact is selected or cleared */
  onChange: (contact: ContactInfo | null) => void
  /** Filter contacts by stakeholder types (e.g., ['BROKER', 'CONTRACTOR']) */
  stakeholderTypes?: StakeholderType[]
  /** Whether to include team members in the dropdown */
  includeTeamMembers?: boolean
  /** If true, ONLY show team members (no external contacts from directory) */
  teamMembersOnly?: boolean
  /** Filter team members by user type (e.g., 'PROJECT_MANAGER', 'ADMINISTRATOR') - applied server-side */
  userType?: string
  /** Whether the field is required */
  required?: boolean
  /** Whether to show validation errors */
  showError?: boolean
  /** Whether the field is disabled/read-only */
  disabled?: boolean
  /** Placeholder text for the dropdown */
  placeholder?: string
}

// Transform function to unify contact/team member data
interface UnifiedContact {
  id: number
  firstName: string
  lastName: string
  email: string
  phone?: string
  companyName?: string
  companyAddress?: string
  source: 'contact' | 'team_member'
}

// Module-level cache for ContactAutocomplete to prevent refetching
// Key: JSON string of stakeholderTypes + teamMembersOnly + includeTeamMembers
const contactAutocompleteCache: Map<string, UnifiedContact[]> = new Map()

const transformContactToUnified = (contact: Contact): UnifiedContact => {
  const nameParts = (contact.contact_object.name || '').split(' ')
  const firstName = nameParts[0] || ''
  const lastName = nameParts.slice(1).join(' ') || ''

  // Build address from structured fields if available
  let companyAddress = ''
  if (contact.contact_object.company_street_address) {
    const parts = []
    if (contact.contact_object.company_street_address)
      parts.push(contact.contact_object.company_street_address)
    if (contact.contact_object.company_city)
      parts.push(contact.contact_object.company_city)
    if (contact.contact_object.company_state)
      parts.push(contact.contact_object.company_state)
    if (contact.contact_object.company_zip_code)
      parts.push(contact.contact_object.company_zip_code)
    companyAddress = parts.join(', ')
  } else if (contact.contact_object.company_address) {
    companyAddress = contact.contact_object.company_address
  }

  return {
    id: contact.id,
    firstName,
    lastName,
    email: contact.contact_object.email,
    phone: contact.contact_object.phone_number,
    companyName: contact.contact_object.company_name,
    companyAddress,
    source: 'contact',
  }
}

const transformTeamMemberToUnified = (member: User): UnifiedContact => {
  // Build address from company structured fields if available
  let companyAddress = ''
  if (member.company) {
    if (member.company.street_address) {
      const parts = []
      if (member.company.street_address)
        parts.push(member.company.street_address)
      if (member.company.city) parts.push(member.company.city)
      if (member.company.state) parts.push(member.company.state)
      if (member.company.zip_code) parts.push(member.company.zip_code)
      companyAddress = parts.join(', ')
    } else if (member.company.address) {
      companyAddress = member.company.address
    }
  }

  return {
    id: member.id,
    firstName: member.first_name || '',
    lastName: member.last_name || '',
    email: member.email,
    phone: member.phone_number,
    companyName: member.company?.name,
    companyAddress,
    source: 'team_member',
  }
}

/**
 * ContactAutocomplete - A contact selection component with 3-box layout
 *
 * Features:
 * - Box 1: Name dropdown (first/last name selection from Directory)
 * - Box 2: Email/Phone display (clickable mailto/tel links)
 * - Box 3: Company name and full address display
 *
 * If no contact is found, allows manual entry with option to save to Directory.
 */
const ContactAutocomplete: React.FC<ContactAutocompleteProps> = ({
  label,
  value,
  onChange,
  stakeholderTypes,
  includeTeamMembers = false,
  teamMembersOnly = false,
  userType,
  required = false,
  showError = false,
  disabled = false,
  placeholder = 'Select or type to search...',
}) => {
  const { user: currentUser } = useUser()
  const [allContacts, setAllContacts] = useState<UnifiedContact[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showManualEntry, setShowManualEntry] = useState(false)
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false)
  const [manualData, setManualData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  })
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  // Ref to track the last cache key we fetched for
  const lastFetchedCacheKeyRef = useRef<string | null>(null)

  // Create a stable cache key from parameters
  const cacheKey = useMemo(() => {
    const sortedTypes = [...(stakeholderTypes || [])].sort().join(',')
    return `${sortedTypes}|${includeTeamMembers}|${teamMembersOnly}|${userType || ''}`
  }, [stakeholderTypes, includeTeamMembers, teamMembersOnly, userType])

  // Fetch contacts and optionally team members on mount
  useEffect(() => {
    // Check if we've already fetched for this cache key
    if (lastFetchedCacheKeyRef.current === cacheKey) {
      return
    }

    // Check module-level cache first
    const cachedData = contactAutocompleteCache.get(cacheKey)
    if (cachedData) {
      setAllContacts(cachedData)
      setIsLoading(false)
      lastFetchedCacheKeyRef.current = cacheKey
      return
    }

    const fetchData = async () => {
      setIsLoading(true)
      try {
        // If teamMembersOnly, skip fetching contacts from directory
        const shouldFetchContacts = !teamMembersOnly
        const shouldFetchTeamMembers = includeTeamMembers || teamMembersOnly

        const promises: Promise<Contact[] | User[]>[] = []

        if (shouldFetchContacts) {
          promises.push(userService.getContacts())
        }
        if (shouldFetchTeamMembers) {
          promises.push(
            userService.getTeamMembers(
              userType ? { user_type: userType } : undefined,
            ),
          )
        }

        const results = await Promise.all(promises)

        let contacts: Contact[] = []
        let teamMembers: User[] = []

        if (teamMembersOnly) {
          // Only team members, no contacts
          teamMembers = results[0] as User[]
        } else {
          contacts = results[0] as Contact[]
          teamMembers = shouldFetchTeamMembers ? (results[1] as User[]) : []
        }

        // Transform and filter contacts (skip if teamMembersOnly)
        let unifiedContacts: UnifiedContact[] = []
        if (!teamMembersOnly) {
          unifiedContacts = contacts
            .filter((c) => {
              if (!stakeholderTypes || stakeholderTypes.length === 0)
                return true
              const hasMatchingStakeholder =
                c.contact_object.stakeholder_types.some((t) =>
                  stakeholderTypes.includes(t as StakeholderType),
                )
              // For BROKER contacts, only show Administrators (user_type = OWNER)
              if (
                hasMatchingStakeholder &&
                stakeholderTypes.includes('BROKER' as StakeholderType)
              ) {
                return c.contact_object.user_type === 'OWNER'
              }
              return hasMatchingStakeholder
            })
            .map(transformContactToUnified)
        }

        // Add team members if included or teamMembersOnly
        if (shouldFetchTeamMembers) {
          // Use current user's company as fallback for team members without company data
          const transformWithCompanyFallback = (
            member: User,
          ): UnifiedContact => {
            const baseTransform = transformTeamMemberToUnified(member)
            // If team member doesn't have company info, use current user's company
            if (!baseTransform.companyName && currentUser?.company) {
              let fallbackAddress = ''
              if (currentUser.company.street_address) {
                const parts = []
                if (currentUser.company.street_address)
                  parts.push(currentUser.company.street_address)
                if (currentUser.company.city)
                  parts.push(currentUser.company.city)
                if (currentUser.company.state)
                  parts.push(currentUser.company.state)
                if (currentUser.company.zip_code)
                  parts.push(currentUser.company.zip_code)
                fallbackAddress = parts.join(', ')
              } else if (currentUser.company.address) {
                fallbackAddress = currentUser.company.address
              }
              return {
                ...baseTransform,
                companyName: currentUser.company.name,
                companyAddress: fallbackAddress,
              }
            }
            return baseTransform
          }

          const unifiedTeamMembers = teamMembers
            .filter((m) => {
              // For contractor field, only show OWNER user type
              const isContractorField = stakeholderTypes?.includes(
                'CONTRACTOR' as StakeholderType,
              )
              if (isContractorField) {
                // Treat missing user_type as OWNER (backend default)
                const userType = m.user_type || 'OWNER'
                if (userType !== 'OWNER') {
                  return false
                }
              }

              if (!stakeholderTypes || stakeholderTypes.length === 0)
                return true
              return (m.stakeholder_types || []).some((t) =>
                stakeholderTypes.includes(t as StakeholderType),
              )
            })
            .map(transformWithCompanyFallback)

          // Deduplicate by email (only needed if we have both contacts and team members)
          if (!teamMembersOnly) {
            const existingEmails = new Set(unifiedContacts.map((c) => c.email))
            const uniqueTeamMembers = unifiedTeamMembers.filter(
              (m) => !existingEmails.has(m.email),
            )
            unifiedContacts = [...unifiedContacts, ...uniqueTeamMembers]
          } else {
            unifiedContacts = unifiedTeamMembers
          }
        }

        // Cache the result
        contactAutocompleteCache.set(cacheKey, unifiedContacts)
        lastFetchedCacheKeyRef.current = cacheKey

        setAllContacts(unifiedContacts)
      } catch (error) {
        console.error('Failed to fetch contacts:', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [
    cacheKey,
    includeTeamMembers,
    teamMembersOnly,
    userType,
    stakeholderTypes,
    currentUser,
  ])

  // Convert contacts to MultiSelect options
  const contactOptions: Option[] = useMemo(
    () =>
      allContacts.map((contact) => ({
        label:
          `${contact.firstName} ${contact.lastName}`.trim() || contact.email,
        value: contact.id.toString(),
        name: `${contact.firstName} ${contact.lastName}`.trim(),
        email: contact.email,
        companyName: contact.companyName,
      })),
    [allContacts],
  )

  // Get the current value as Option for MultiSelect
  const selectedOption: Option | null = useMemo(() => {
    if (!value) return null
    if (!value.isFromDirectory || !value.id) return null
    return {
      label: `${value.firstName} ${value.lastName}`.trim() || value.email,
      value: value.id.toString(),
      name: `${value.firstName} ${value.lastName}`.trim(),
      email: value.email,
    }
  }, [value])

  // Handle selection from dropdown
  const handleSelect = useCallback(
    (selected: Option | Option[] | null) => {
      const option = Array.isArray(selected) ? selected[0] : selected
      if (!option) {
        onChange(null)
        return
      }

      const contact = allContacts.find((c) => c.id.toString() === option.value)
      if (contact) {
        onChange({
          id: contact.id,
          firstName: contact.firstName,
          lastName: contact.lastName,
          email: contact.email,
          phone: contact.phone,
          companyName: contact.companyName,
          companyAddress: contact.companyAddress,
          isFromDirectory: true,
        })
      }
    },
    [allContacts, onChange],
  )

  // Handle clearing the selection
  const handleClear = useCallback(() => {
    onChange(null)
    setShowManualEntry(false)
    setManualData({ firstName: '', lastName: '', email: '', phone: '' })
  }, [onChange])

  // Handle switching to manual entry mode
  const handleManualEntryClick = useCallback(() => {
    setShowManualEntry(true)
    setSaveError(null)
  }, [])

  // Handle manual entry field changes
  const handleManualChange = useCallback(
    (field: keyof typeof manualData, fieldValue: string) => {
      setManualData((prev) => ({ ...prev, [field]: fieldValue }))
    },
    [],
  )

  // Handle using the manually entered contact (shows save confirmation)
  const handleUseManualContact = useCallback(() => {
    if (!manualData.email.trim()) {
      setSaveError('Email is required')
      return
    }
    // Show confirmation dialog
    setShowSaveConfirmation(true)
  }, [manualData.email])

  // Handle saving manual contact to directory
  const handleSaveToDirectory = useCallback(async () => {
    setIsSaving(true)
    setSaveError(null)

    try {
      const contactData: ContactCreate = {
        email: manualData.email.trim(),
        name:
          `${manualData.firstName} ${manualData.lastName}`.trim() || undefined,
        phone_number: manualData.phone || undefined,
        stakeholder_types: stakeholderTypes || [],
      }

      await userService.addContact(contactData)

      // After saving, use the contact
      onChange({
        firstName: manualData.firstName,
        lastName: manualData.lastName,
        email: manualData.email,
        phone: manualData.phone,
        isFromDirectory: true, // Now it's in directory
      })

      setShowManualEntry(false)
      setShowSaveConfirmation(false)
      setManualData({ firstName: '', lastName: '', email: '', phone: '' })

      // Refresh contacts list and invalidate cache
      const contacts = await userService.getContacts()
      const unifiedContacts = contacts
        .filter((c) => {
          if (!stakeholderTypes || stakeholderTypes.length === 0) return true
          return c.contact_object.stakeholder_types.some((t) =>
            stakeholderTypes.includes(t as StakeholderType),
          )
        })
        .map(transformContactToUnified)
      setAllContacts(unifiedContacts)

      // Update the cache with new data
      contactAutocompleteCache.set(cacheKey, unifiedContacts)
    } catch (error) {
      console.error('Failed to save contact:', error)
      setSaveError('Failed to save contact to directory')
    } finally {
      setIsSaving(false)
    }
  }, [manualData, stakeholderTypes, onChange, cacheKey])

  // Handle using contact without saving to directory
  const handleUseWithoutSaving = useCallback(() => {
    onChange({
      firstName: manualData.firstName,
      lastName: manualData.lastName,
      email: manualData.email,
      phone: manualData.phone,
      isFromDirectory: false,
    })

    setShowManualEntry(false)
    setShowSaveConfirmation(false)
    setManualData({ firstName: '', lastName: '', email: '', phone: '' })
  }, [manualData, onChange])

  const hasError = showError && required && !value

  // If a contact is selected (from directory), show the 3-box layout
  if (value && !showManualEntry) {
    return (
      <div className={css(styles.container)}>
        <label className={css(styles.label)}>
          {label}
          {required && ' *'}
        </label>

        <div className={css(styles.threeBoxLayout)}>
          {/* Box 1: Name (read-only with lock icon) */}
          <div className={css(styles.box, styles.nameBox)}>
            <div className={css(styles.boxLabel)}>Name</div>
            <div className={css(styles.boxValue, styles.lockedValue)}>
              <span>
                {`${value.firstName} ${value.lastName}`.trim() || '-'}
              </span>
            </div>
          </div>

          {/* Box 2: Email/Phone (removable but not editable) */}
          <div className={css(styles.box, styles.contactBox)}>
            <div className={css(styles.boxLabel)}>Contact</div>
            <div className={css(styles.contactItems)}>
              {value.email && (
                <div className={css(styles.removableItem)}>
                  <a
                    href={`mailto:${value.email}`}
                    className={css(styles.clickableLink)}
                  >
                    {value.email}
                  </a>
                  {!disabled && (
                    <button
                      type="button"
                      className={css(styles.removeItemButton)}
                      onClick={() => onChange({ ...value, email: '' })}
                      title="Remove email"
                    >
                      <i className="fa-solid fa-times" />
                    </button>
                  )}
                </div>
              )}
              {value.phone && (
                <div className={css(styles.removableItem)}>
                  <a
                    href={`tel:${value.phone}`}
                    className={css(styles.clickableLink)}
                  >
                    {value.phone}
                  </a>
                  {!disabled && (
                    <button
                      type="button"
                      className={css(styles.removeItemButton)}
                      onClick={() => onChange({ ...value, phone: '' })}
                      title="Remove phone"
                    >
                      <i className="fa-solid fa-times" />
                    </button>
                  )}
                </div>
              )}
              {!value.email && !value.phone && (
                <span className={css(styles.emptyContact)}>
                  No contact info
                </span>
              )}
            </div>
          </div>

          {/* Box 3: Company/Address (read-only with lock icon) */}
          <div className={css(styles.box, styles.addressBox)}>
            <div className={css(styles.boxLabel)}>Company / Address</div>
            <div className={css(styles.boxValue)}>
              {value.companyName && (
                <div className={css(styles.companyName, styles.lockedValue)}>
                  <span>{value.companyName}</span>
                </div>
              )}
              {value.companyAddress && (
                <div className={css(styles.companyAddress, styles.lockedValue)}>
                  <span>{value.companyAddress}</span>
                </div>
              )}
              {!value.companyName && !value.companyAddress && '-'}
            </div>
          </div>

          {/* Remove button */}
          {!disabled && (
            <button
              type="button"
              className={css(styles.removeButton)}
              onClick={handleClear}
              title="Remove contact"
            >
              <i className="fa-solid fa-times" />
            </button>
          )}
        </div>
      </div>
    )
  }

  // Show manual entry form
  if (showManualEntry) {
    return (
      <div className={css(styles.container)}>
        <label className={css(styles.label)}>
          {label}
          {required && ' *'}
        </label>

        <div className={css(styles.manualEntryForm)}>
          <div className={css(styles.manualEntryHeader)}>
            <span>Enter contact information manually</span>
            <button
              type="button"
              className={css(styles.backLink)}
              onClick={() => setShowManualEntry(false)}
            >
              Back to search
            </button>
          </div>

          <div className={css(styles.manualEntryGrid)}>
            <div className={css(styles.manualField)}>
              <label className={css(styles.manualLabel)}>First Name</label>
              <input
                type="text"
                className={css(styles.manualInput)}
                value={manualData.firstName}
                onChange={(e) =>
                  handleManualChange('firstName', e.target.value)
                }
                placeholder="First name"
                disabled={disabled}
              />
            </div>

            <div className={css(styles.manualField)}>
              <label className={css(styles.manualLabel)}>Last Name</label>
              <input
                type="text"
                className={css(styles.manualInput)}
                value={manualData.lastName}
                onChange={(e) => handleManualChange('lastName', e.target.value)}
                placeholder="Last name"
                disabled={disabled}
              />
            </div>

            <div className={css(styles.manualField)}>
              <label className={css(styles.manualLabel)}>Email *</label>
              <input
                type="email"
                className={css(styles.manualInput)}
                value={manualData.email}
                onChange={(e) => handleManualChange('email', e.target.value)}
                placeholder="email@example.com"
                disabled={disabled}
              />
            </div>

            <div className={css(styles.manualField)}>
              <label className={css(styles.manualLabel)}>Phone</label>
              <input
                type="tel"
                className={css(styles.manualInput)}
                value={manualData.phone}
                onChange={(e) => handleManualChange('phone', e.target.value)}
                placeholder="(555) 123-4567"
                disabled={disabled}
              />
            </div>
          </div>

          {saveError && (
            <div className={css(styles.saveError)}>{saveError}</div>
          )}

          <div className={css(styles.manualEntryActions)}>
            <button
              type="button"
              className={css(styles.useButton)}
              onClick={handleUseManualContact}
              disabled={disabled || !manualData.email.trim()}
            >
              Use Contact
            </button>
          </div>
        </div>

        {/* Save Confirmation Dialog - shared component */}
        {showSaveConfirmation && (
          <SaveToDirectoryDialog
            email={manualData.email}
            name={
              `${manualData.firstName} ${manualData.lastName}`.trim() ||
              undefined
            }
            phoneNumber={manualData.phone || undefined}
            stakeholderTypes={stakeholderTypes}
            onConfirm={handleSaveToDirectory}
            onSkip={handleUseWithoutSaving}
            externalSave={true}
            isLoading={isSaving}
            showPreview={false}
          />
        )}
      </div>
    )
  }

  // Default: Show dropdown search
  return (
    <div className={css(styles.container)}>
      <label className={css(styles.label)}>
        {label}
        {required && ' *'}
      </label>

      <MultiSelect
        isDisabled={disabled}
        isLoading={isLoading}
        placeholder={placeholder}
        options={contactOptions}
        value={selectedOption}
        onChange={handleSelect}
        isMulti={false}
        showEmailInOption
      />

      {hasError && (
        <div className={css(styles.errorMessage)}>
          Please select a {label.toLowerCase()}.
        </div>
      )}

      {!disabled && !value && (
        <button
          type="button"
          className={css(styles.manualEntryLink)}
          onClick={handleManualEntryClick}
        >
          <i className="fa-solid fa-plus" /> Enter contact manually
        </button>
      )}
    </div>
  )
}

const styles = StyleSheet.create({
  container: {
    marginBottom: '16px',
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#374151',
  },
  errorMessage: {
    color: '#DC2626',
    fontSize: '12px',
    marginTop: '4px',
    fontWeight: 500,
  },
  manualEntryLink: {
    marginTop: '8px',
    padding: '0',
    background: 'none',
    border: 'none',
    color: '#2563EB',
    fontSize: '13px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    ':hover': {
      textDecoration: 'underline',
    },
  },

  // 3-box layout styles
  threeBoxLayout: {
    display: 'grid',
    gridTemplateColumns: '1fr 1.5fr 1.5fr auto',
    gap: '12px',
    alignItems: 'stretch',
    backgroundColor: '#F0F9FF',
    border: '1px solid #BAE6FD',
    borderRadius: '8px',
    padding: '12px',
  },
  box: {
    padding: '8px 12px',
    backgroundColor: 'white',
    borderRadius: '6px',
    border: '1px solid #E5E7EB',
  },
  nameBox: {},
  contactBox: {},
  addressBox: {},
  boxLabel: {
    fontSize: '11px',
    fontWeight: 600,
    color: '#6B7280',
    textTransform: 'uppercase',
    marginBottom: '4px',
  },
  boxValue: {
    fontSize: '14px',
    color: '#111827',
  },
  lockedValue: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: '#374151',
    ':nth-child(1n) > i': {
      fontSize: '10px',
      color: '#9CA3AF',
    },
  },
  contactItems: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  removableItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
  },
  removeItemButton: {
    backgroundColor: 'transparent',
    border: 'none',
    color: '#9CA3AF',
    cursor: 'pointer',
    padding: '2px 6px',
    borderRadius: '4px',
    fontSize: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ':hover': {
      backgroundColor: '#FEE2E2',
      color: '#DC2626',
    },
  },
  emptyContact: {
    color: '#9CA3AF',
    fontStyle: 'italic',
    fontSize: '13px',
  },
  clickableLink: {
    color: '#2563EB',
    textDecoration: 'none',
    ':hover': {
      textDecoration: 'underline',
    },
  },
  companyName: {
    fontWeight: 500,
  },
  companyAddress: {
    fontSize: '13px',
    color: '#6B7280',
    marginTop: '2px',
  },
  removeButton: {
    padding: '8px',
    background: 'none',
    border: 'none',
    color: '#9CA3AF',
    cursor: 'pointer',
    fontSize: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'center',
    borderRadius: '4px',
    ':hover': {
      color: '#DC2626',
      backgroundColor: '#FEE2E2',
    },
  },

  // Manual entry form styles
  manualEntryForm: {
    backgroundColor: '#F9FAFB',
    border: '1px solid #E5E7EB',
    borderRadius: '8px',
    padding: '16px',
  },
  manualEntryHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#374151',
  },
  backLink: {
    padding: '0',
    background: 'none',
    border: 'none',
    color: '#2563EB',
    fontSize: '13px',
    cursor: 'pointer',
    ':hover': {
      textDecoration: 'underline',
    },
  },
  manualEntryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '12px',
  },
  manualField: {
    display: 'flex',
    flexDirection: 'column',
  },
  manualLabel: {
    fontSize: '12px',
    fontWeight: 500,
    color: '#6B7280',
    marginBottom: '4px',
  },
  manualInput: {
    padding: '10px 12px',
    border: '1px solid #D1D5DB',
    borderRadius: '6px',
    fontSize: '14px',
    ':focus': {
      outline: 'none',
      borderColor: '#3B82F6',
      boxShadow: '0 0 0 2px rgba(59, 130, 246, 0.1)',
    },
  },
  manualEntryActions: {
    marginTop: '16px',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  useButton: {
    padding: '10px 20px',
    backgroundColor: '#1C64F2',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    ':hover': {
      backgroundColor: '#1955CC',
    },
    ':disabled': {
      backgroundColor: '#9CA3AF',
      cursor: 'not-allowed',
    },
  },
  saveError: {
    marginTop: '12px',
    padding: '8px 12px',
    backgroundColor: '#FEE2E2',
    color: '#DC2626',
    borderRadius: '4px',
    fontSize: '13px',
  },
})

export default ContactAutocomplete
